import React, { useState, useEffect, useRef } from 'react';
import { useOutletContext } from 'react-router-dom';
import { CheckCircle, AlertTriangle, Camera, Trash2, MailCheck, MailWarning, Send } from 'lucide-react';
import api from '../api';
import Avatar from '../components/Avatar';
import Spinner from '../components/Spinner';

const ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
const MAX_FILE_SIZE = 5 * 1024 * 1024;

const ProfilePage = () => {
    const { user, setUser, fetchSessions } = useOutletContext();
    const [profileForm, setProfileForm] = useState({ name: '' });
    const [alert, setAlert] = useState(null); // { type: 'success' | 'error', message: '' }
    const [saving, setSaving] = useState(false);
    const [uploading, setUploading] = useState(false);
    // Пока файл летит на сервер, показываем выбранную картинку: ждать ответа, глядя на
    // прежний аватар, выглядит так, будто нажатие не сработало.
    const [preview, setPreview] = useState(null);
    // Общая занятость почтовых кнопок: обе отправляют письмо, и второе нажатие подряд
    // получило бы отказ по ограничению частоты вместо письма.
    const [mailBusy, setMailBusy] = useState(false);
    const fileInputRef = useRef(null);
    const savedName = user?.name || '';

    useEffect(() => {
        // Обновление статуса почты при возврате во вкладку не должно стирать
        // ещё не сохранённое имя в форме.
        setProfileForm({ name: savedName });
    }, [savedName]);

    // Ссылка на предпросмотр живёт в памяти вкладки, пока её не освободить.
    useEffect(() => () => { if (preview) URL.revokeObjectURL(preview); }, [preview]);

    const showAlert = (type, message) => {
        setAlert({ type, message });
        setTimeout(() => setAlert(null), 5000);
    };

    const handleProfileSave = () => {
        if (saving) return;
        setSaving(true);
        api.put('/api/users/me', profileForm)
            .then(res => {
                setUser(res.data);
                showAlert('success', 'Профиль успешно обновлен');
                fetchSessions(); // Update sessions to reflect new name/avatar
            })
            .catch(() => showAlert('error', 'Не удалось сохранить профиль'))
            .finally(() => setSaving(false));
    };

    const handleAvatarPick = (event) => {
        const file = event.target.files && event.target.files[0];
        // Тот же файл, выбранный второй раз подряд, иначе не вызовет событие.
        event.target.value = '';
        if (!file) return;

        if (!ACCEPTED_TYPES.includes(file.type)) {
            showAlert('error', 'Подойдёт изображение JPEG, PNG или WebP');
            return;
        }
        if (file.size > MAX_FILE_SIZE) {
            showAlert('error', 'Файл больше 5 МБ');
            return;
        }

        const objectUrl = URL.createObjectURL(file);
        setPreview(objectUrl);
        setUploading(true);

        const body = new FormData();
        body.append('file', file);

        api.post('/api/users/me/avatar', body, {
            // Заголовок снимается намеренно, и null здесь не описка.
            //
            // У клиента в умолчаниях стоит application/json, а axios, увидев такой тип,
            // не отправляет FormData как есть, а переписывает её в JSON - файл при этом
            // превращается в строку с именем. Пустое же значение оставляет тип на усмотрение
            // браузера, и тот проставляет multipart вместе с границей частей, без которой
            // разбор на сервере не состоится. Именно null, а не undefined: undefined
            // при слиянии с настройками клиента отбрасывается, и умолчание остаётся в силе.
            headers: { 'Content-Type': null },
        })
            .then(res => {
                setUser(res.data);
                showAlert('success', 'Аватар обновлён');
                fetchSessions();
            })
            .catch(err => {
                const status = err.response?.status;
                if (status === 503) {
                    showAlert('error', 'Хранилище аватаров не настроено. Обратитесь к администратору.');
                } else if (status === 415) {
                    showAlert('error', 'Подойдёт изображение JPEG, PNG или WebP');
                } else if (status === 413) {
                    showAlert('error', 'Файл больше 5 МБ');
                } else {
                    showAlert('error', 'Не удалось загрузить аватар');
                }
            })
            .finally(() => {
                setUploading(false);
                setPreview(current => {
                    if (current) URL.revokeObjectURL(current);
                    return null;
                });
            });
    };

    const handleEmailAction = (request) => {
        if (mailBusy) return;
        setMailBusy(true);
        request()
            .finally(() => setMailBusy(false));
    };

    const handleResendVerification = () => handleEmailAction(() =>
        api.post('/api/users/me/email/verify/resend')
            .then(() => showAlert('success', `Письмо со ссылкой отправлено на ${user.email}`))
            .catch(err => showAlert('error', err.response?.status === 429
                ? 'Письмо уже отправлено. Попробуйте через минуту.'
                : 'Не удалось отправить письмо')));

    const handleRequestEmailChange = () => {
        if (!window.confirm('Отправить письмо для смены адреса на текущую почту ' + user.email + '?')) return;
        handleEmailAction(() =>
            api.post('/api/users/me/email/change/request')
                .then(() => showAlert('success', `Письмо отправлено на ${user.email}. Смена продолжится по ссылке из письма.`))
                .catch(err => showAlert('error', err.response?.status === 429
                    ? 'Письмо уже отправлено. Попробуйте через минуту.'
                    : 'Не удалось отправить письмо')));
    };

    const handleAvatarDelete = () => {
        if (uploading) return;
        if (!window.confirm('Удалить аватар?')) return;

        setUploading(true);
        api.delete('/api/users/me/avatar')
            .then(res => {
                setUser(res.data);
                showAlert('success', 'Аватар удалён');
                fetchSessions();
            })
            .catch(() => showAlert('error', 'Не удалось удалить аватар'))
            .finally(() => setUploading(false));
    };

    if (!user) return null;

    return (
        <div>
            <div className="mb-8">
                <h1 className="text-[28px] font-semibold text-gray-900 m-0">Профиль</h1>
            </div>

            {alert && (
                <div className={`p-3 rounded-lg mb-5 text-sm flex items-start gap-2.5 ${alert.type === 'success' ? 'bg-emerald-50 text-emerald-800 border border-emerald-200' : 'bg-red-50 text-red-800 border border-red-200'}`}>
                    {alert.type === 'success' ? <CheckCircle size={20} /> : <AlertTriangle size={20} />}
                    <span>{alert.message}</span>
                </div>
            )}

            <div className="bg-white rounded-xl p-6 mb-6 shadow-sm border border-gray-200">
                <div className="flex flex-col items-center pb-6 mb-6 border-b border-gray-100">
                    <button
                        type="button"
                        onClick={() => !uploading && fileInputRef.current?.click()}
                        disabled={uploading}
                        aria-label="Изменить аватар"
                        className="group relative rounded-full focus:outline-none focus-visible:ring-4 focus-visible:ring-blue-600/20 disabled:cursor-default"
                    >
                        <Avatar
                            src={preview || user.avatarUrl}
                            name={user.name}
                            email={user.email}
                            size={140}
                            className="ring-4 ring-white shadow-md"
                        />

                        <span
                            className={`absolute inset-0 rounded-full bg-black/60 text-white flex flex-col items-center justify-center gap-1.5 px-3 text-center transition-opacity
                                ${uploading ? 'opacity-100' : 'opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100'}`}
                        >
                            {uploading ? (
                                <Spinner size={26} />
                            ) : (
                                <>
                                    <Camera size={24} />
                                    <span className="text-[11px] leading-tight font-medium">
                                        Нажмите, чтобы изменить аватар
                                    </span>
                                </>
                            )}
                        </span>
                    </button>

                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/jpeg,image/png,image/webp"
                        className="hidden"
                        onChange={handleAvatarPick}
                    />

                    <div className="mt-4 text-center">
                        <div className="text-lg font-semibold text-gray-900">{user.name || 'Без имени'}</div>
                        <div className="text-sm text-gray-500">{user.email}</div>
                    </div>

                    {user.avatarUrl && (
                        <button
                            type="button"
                            onClick={handleAvatarDelete}
                            disabled={uploading}
                            className="mt-3 inline-flex items-center gap-1.5 bg-transparent text-gray-500 px-3 py-1.5 border border-gray-200 rounded-md text-[13px] font-medium transition-all enabled:cursor-pointer enabled:hover:text-red-600 enabled:hover:border-red-300 enabled:hover:bg-red-50 disabled:opacity-40"
                        >
                            <Trash2 size={14} />
                            Удалить аватар
                        </button>
                    )}

                    <p className="mt-3 text-xs text-gray-400">JPEG, PNG или WebP, до 5 МБ</p>
                </div>

                <h3 className="mt-0 mb-5 text-lg font-semibold text-gray-900">Основная информация</h3>
                <div className="mb-4 flex flex-col gap-1.5 text-left">
                    <label className="text-sm font-medium text-gray-700">Почта (Email)</label>
                    <input className="px-3.5 py-2.5 border border-gray-300 rounded-lg text-[15px] transition-all bg-gray-50 text-gray-500 focus:outline-none" type="text" value={user.email} disabled />
                </div>
                <div className="mb-4 flex flex-col gap-1.5 text-left">
                    <label className="text-sm font-medium text-gray-700">Имя пользователя</label>
                    <input
                        className="px-3.5 py-2.5 border border-gray-300 rounded-lg text-[15px] transition-all bg-white focus:border-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-600/10"
                        type="text"
                        placeholder="Ваше имя"
                        value={profileForm.name}
                        onChange={e => setProfileForm({ ...profileForm, name: e.target.value })}
                    />
                </div>
                <div className="flex gap-3 mt-4">
                    <button
                        className="bg-blue-600 text-white px-4 py-2.5 border-none rounded-lg text-sm font-medium transition-colors flex items-center gap-2 enabled:cursor-pointer enabled:hover:bg-blue-700 disabled:bg-blue-300 disabled:cursor-not-allowed"
                        onClick={handleProfileSave}
                        disabled={saving}
                    >
                        {saving && <Spinner size={16} />}
                        {saving ? 'Сохраняем…' : 'Сохранить изменения'}
                    </button>
                </div>
            </div>

            <div className="bg-white rounded-xl p-6 mb-6 shadow-sm border border-gray-200">
                <h3 className="mt-0 mb-5 text-lg font-semibold text-gray-900">Адрес почты</h3>

                <div className={`p-3 rounded-lg mb-5 text-sm flex items-start gap-2.5 ${user.emailVerified ? 'bg-emerald-50 text-emerald-800 border border-emerald-200' : 'bg-amber-50 text-amber-800 border border-amber-200'}`}>
                    {user.emailVerified ? <MailCheck size={20} /> : <MailWarning size={20} />}
                    <span>
                        {user.emailVerified
                            ? <>Адрес <span className="font-medium">{user.email}</span> подтверждён.</>
                            : <>Адрес <span className="font-medium">{user.email}</span> не подтверждён. Подтвердите, что этот адрес принадлежит вам.</>}
                    </span>
                </div>

                <div className="flex gap-3 flex-wrap">
                    {!user.emailVerified && (
                        <button
                            className="bg-blue-600 text-white px-4 py-2.5 border-none rounded-lg text-sm font-medium transition-colors flex items-center gap-2 enabled:cursor-pointer enabled:hover:bg-blue-700 disabled:bg-blue-300 disabled:cursor-not-allowed"
                            onClick={handleResendVerification}
                            disabled={mailBusy}
                        >
                            {mailBusy ? <Spinner size={16} /> : <Send size={16} />}
                            Отправить письмо ещё раз
                        </button>
                    )}
                    <button
                        className="bg-white text-gray-700 px-4 py-2.5 border border-gray-300 rounded-lg text-sm font-medium transition-all flex items-center gap-2 enabled:cursor-pointer enabled:hover:bg-gray-50 enabled:hover:border-gray-400 disabled:opacity-50 disabled:cursor-not-allowed"
                        onClick={handleRequestEmailChange}
                        disabled={mailBusy}
                    >
                        Сменить адрес
                    </button>
                </div>

                <p className="text-[13px] text-gray-500 mt-3 mb-0">
                    Письмо для смены адреса уходит на текущую почту: сменить её может только тот,
                    кто владеет ящиком, указанным в аккаунте сейчас.
                </p>
            </div>
        </div>
    );
};

export default ProfilePage;
